import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { AddCircle as AddCircleIcon, Cancel as CancelIcon } from "@material-ui/icons";
import DataTable from "react-data-table-component";
import { API_POST } from "../../services/api.service";
import Loader from "components/Loader/Loader";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

const useStyles = makeStyles((theme) => ({
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  subcategoryInput: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
  subcategoryItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  roleCheckboxContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const SupportCategory = ({ user, match }) => {
  const classes = useStyles();
  const navigate = (url) => {
    window.location.href = url;
  };
  const id = match.params.id;

  const [state, setState] = useState({
    showLoader: false,
    categories: [],
    categoryDetails: {
      categoryName: "",
      subcategories: [], // Now will be array of objects: { name: "", link: "" }
      isActive: true,
      visibleToRoles: {
        owner: false,
        manager: false,
        superAdmin: false,
      },
    },
    newSubcategory: {
      name: "",
      link: "",
    },
  });

  const { showLoader, categories, categoryDetails, newSubcategory } = state;
  const isEditMode = !!id;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setState((prev) => ({ ...prev, showLoader: true }));
      try {
        const categoriesResponse = await API_POST("action/getdata", {
          root: "supportCategories",
          con: {},
        });

        if (id) {
          const editResponse = await API_POST("action/getdata", {
            root: "supportCategories",
            con: { _id: id },
          });

          if (editResponse.data.length > 0) {
            // Convert legacy string subcategories to objects if needed
            const categoryData = editResponse.data[0];
            if (categoryData.subcategories && categoryData.subcategories.length > 0 && typeof categoryData.subcategories[0] === 'string') {
              categoryData.subcategories = categoryData.subcategories.map(name => ({ name, link: '' }));
            }
            
            // Initialize visibleToRoles if not present
            if (!categoryData.visibleToRoles) {
              categoryData.visibleToRoles = {
                owner: false,
                manager: false,
                superAdmin: false,
              };
            }
            
            setState((prev) => ({
              ...prev,
              categoryDetails: categoryData,
            }));
          }
        }

        setState((prev) => ({
          ...prev,
          categories: categoriesResponse.data || [],
          showLoader: false,
        }));
      } catch (error) {
        console.error("Error loading data:", error);
        setState((prev) => ({ ...prev, showLoader: false }));
      }
    };

    loadData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        [name]: value,
      },
    }));
  };

  const handleRoleVisibilityChange = (role) => (e) => {
    setState((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        visibleToRoles: {
          ...prev.categoryDetails.visibleToRoles,
          [role]: e.target.checked,
        },
      },
    }));
  };

  const handleSubcategoryChange = (e) => {
    console.log(e.target);
    console.log(e.target.checked);
    console.log(e.target.name);
    console.log(e.target.value);


    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      newSubcategory: {
        ...prev.newSubcategory,
        [name]: value || e.target.checked,
      },
    }));
  };

  const addSubcategory = () => {
    if (!newSubcategory.name.trim()) return;

    setState((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        subcategories: [
          ...prev.categoryDetails.subcategories,
          {
            name: newSubcategory.name.trim(),
            link: newSubcategory.link.trim(),
          },
        ],
      },
      newSubcategory: { name: "", link: "" },
    }));
  };

  const removeSubcategory = (index) => {
    setState((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        subcategories: prev.categoryDetails.subcategories.filter((_, i) => i !== index),
      },
    }));
  };

  const updateSubcategoryLink = (index, link) => {
    setState((prev) => {
      const updatedSubcategories = [...prev.categoryDetails.subcategories];
      updatedSubcategories[index] = {
        ...updatedSubcategories[index],
        link: link.trim(),
      };
      return {
        ...prev,
        categoryDetails: {
          ...prev.categoryDetails,
          subcategories: updatedSubcategories,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!categoryDetails.categoryName.trim()) {
      alert("Category name is required");
      return;
    }

    setState((prev) => ({ ...prev, showLoader: true }));

    try {
      const payload = {
        ...categoryDetails,
        admin: user.username,
        updatedOn: new Date(),
      };

      if (!isEditMode) {
        payload.createdOn = new Date();
      }

      const endpoint = isEditMode ? "action/update" : "action/addRecord";
      const queryData = isEditMode ? { _id: id } : {};

      await API_POST(endpoint, {
        root: "supportCategories",
        body: payload,
        querydata: queryData,
      });

      navigate("/admin/supportSetting");
    } catch (error) {
      console.error("Error saving category:", error);
    } finally {
      setState((prev) => ({ ...prev, showLoader: false }));
    }
  };

  const openEditModal = (category) => {
    // Convert legacy string subcategories to objects if needed
    let categoryData = { ...category };
    if (categoryData.subcategories && categoryData.subcategories.length > 0 && typeof categoryData.subcategories[0] === 'string') {
      categoryData.subcategories = categoryData.subcategories.map(name => ({ name, link: '' }));
    }
    if (!categoryData.visibleToRoles) {
      categoryData.visibleToRoles = {
        owner: false,
        manager: false,
        superAdmin: false,
      };
    }
    setEditCategory(categoryData);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditCategory(null);
  };

  const handleEditCategoryChange = (e) => {
    const { name, value } = e.target;
    setEditCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditRoleVisibilityChange = (role) => (e) => {
    setEditCategory((prev) => ({
      ...prev,
      visibleToRoles: {
        ...prev.visibleToRoles,
        [role]: e.target.checked,
      },
    }));
  };

  const handleEditSubcategoryChange = (index, field, value) => {
    setEditCategory((prev) => {
      const updatedSubcategories = [...prev.subcategories];
      updatedSubcategories[index] = {
        ...updatedSubcategories[index],
        [field]: value,
      };
      return {
        ...prev,
        subcategories: updatedSubcategories,
      };
    });
  };

  const handleEditRemoveSubcategory = (index) => {
    setEditCategory((prev) => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index),
    }));
  };

  const handleEditAddSubcategory = () => {
    if (!editCategory.newSubcategoryName || !editCategory.newSubcategoryName.trim()) return;
    setEditCategory((prev) => ({
      ...prev,
      subcategories: [
        ...prev.subcategories,
        { name: prev.newSubcategoryName.trim(), link: prev.newSubcategoryLink ? prev.newSubcategoryLink.trim() : "" },
      ],
      newSubcategoryName: "",
      newSubcategoryLink: "",
    }));
  };

  const handleEditNewSubcategoryChange = (e) => {
    const { name, value } = e.target;
    setEditCategory((prev) => ({
      ...prev,
      [name]: value || e.target.checked,
    }));
  };

  const handleEditSave = async () => {
    if (!editCategory.categoryName.trim()) {
      alert("Category name is required");
      return;
    }
    setState((prev) => ({ ...prev, showLoader: true }));
    try {
      const payload = {
        ...editCategory,
        admin: user.username,
        updatedOn: new Date(),
      };
      const endpoint = "action/update";
      const queryData = { _id: editCategory._id };
      await API_POST(endpoint, {
        root: "supportCategories",
        body: payload,
        querydata: queryData,
      });
      // Refresh categories
      const categoriesResponse = await API_POST("action/getdata", {
        root: "supportCategories",
        con: {},
      });
      setState((prev) => ({
        ...prev,
        categories: categoriesResponse.data || [],
        showLoader: false,
      }));
      closeEditModal();
    } catch (error) {
      console.error("Error saving category:", error);
      setState((prev) => ({ ...prev, showLoader: false }));
    }
  };

  const columns = [
    {
      name: "Category Name",
      selector: "categoryName",
      sortable: true,
    },
    {
      name: "Subcategories",
      cell: (row) => (
        <div className={classes.chipContainer}>
          {row.subcategories.map((subcat, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', margin: '4px' }}>
              <Chip label={subcat.name} size="small" />
              {subcat.link && (
                <a href={subcat.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '8px' }}>
                  <small>Link</small>
                </a>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      name: "Visible To",
      cell: (row) => (
        <div>
          {row.visibleToRoles && row.visibleToRoles.owner && <Chip label="Owner" size="small" style={{ margin: '2px' }} />}
          {row.visibleToRoles && row.visibleToRoles.manager && <Chip label="Manager" size="small" style={{ margin: '2px' }} />}
          {row.visibleToRoles && row.visibleToRoles.superAdmin && <Chip label="Super Admin" size="small" style={{ margin: '2px' }} />}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          color="primary"
          size="small"
          onClick={() => openEditModal(row)}
        >
          Edit
        </Button>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  if (!user.is_superuser) {
    return <div>Access denied. Only superusers can manage categories.</div>;
  }

  return (
    <>
      {showLoader && <Loader />}

      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardHeader title={isEditMode ? "Edit Category" : "Add New Category"} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Category Name *"
                    name="categoryName"
                    value={categoryDetails.categoryName}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <label>Subcategories</label>
                  {categoryDetails.subcategories.map((subcat, index) => (
                    <div key={index} className={classes.subcategoryItem}>
                      <Chip
                        label={subcat.name}
                        onDelete={() => removeSubcategory(index)}
                        deleteIcon={<CancelIcon />}
                      />
                      <TextField
                        style={{ marginLeft: '16px', flexGrow: 1 }}
                        placeholder="Support link"
                        value={subcat.link}
                        onChange={(e) => updateSubcategoryLink(index, e.target.value)}
                      />
                    </div>
                  ))}
                  <div className={classes.subcategoryInput}>
                    <TextField
                      fullWidth
                      name="name"
                      value={newSubcategory.name}
                      onChange={handleSubcategoryChange}
                      placeholder="Subcategory name"
                    />
                    <TextField
                      fullWidth
                      name="link"
                      value={newSubcategory.link}
                      onChange={handleSubcategoryChange}
                      placeholder="Support link"
                    />
                    <TextField
                      fullWidth
                      name="videoUrl"
                      value={newSubcategory.videoUrl}
                      onChange={handleSubcategoryChange}
                      placeholder="Video URL"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isPaid"
                          checked={newSubcategory.isPaid || false}
                          onChange={handleSubcategoryChange}
                          color="primary"
                        />
                      }
                      label="isPaid"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="needFeedback"
                          checked={newSubcategory.needFeedback || false}
                          onChange={handleSubcategoryChange}
                          color="primary"
                        />
                      }
                      label="needFeedback"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="showExternally"
                          checked={newSubcategory.showExternally || false}
                          onChange={handleSubcategoryChange}
                          color="primary"
                        />
                      }
                      label="showExternally"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="scheduleMeeting"
                          checked={newSubcategory.scheduleMeeting || false}
                          onChange={handleSubcategoryChange}
                          color="primary"
                        />
                      }
                      label="scheduleMeeting"
                    />


                    
                    <IconButton
                      color="primary"
                      onClick={addSubcategory}
                      disabled={!newSubcategory.name.trim()}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <label>Visible To Roles</label>
                  <div className={classes.roleCheckboxContainer}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={categoryDetails.visibleToRoles.owner || false}
                          onChange={handleRoleVisibilityChange('owner')}
                          color="primary"
                        />
                      }
                      label="Owner"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={categoryDetails.visibleToRoles.manager || false}
                          onChange={handleRoleVisibilityChange('manager')}
                          color="primary"
                        />
                      }
                      label="Manager"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={categoryDetails.visibleToRoles.superAdmin || false}
                          onChange={handleRoleVisibilityChange('superAdmin')}
                          color="primary"
                        />
                      }
                      label="Super Admin"
                    />
                  </div>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Button color="secondary" onClick={() => navigate("/admin/supportSetting")}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleSave}>
                {isEditMode ? "Update" : "Save"}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Existing Categories" />
            <CardContent>
              <DataTable
                columns={columns}
                data={categories}
                pagination
                highlightOnHover
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50]}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={closeEditModal} fullWidth maxWidth="xl">
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          {editCategory && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category Name *"
                  name="categoryName"
                  value={editCategory.categoryName}
                  onChange={handleEditCategoryChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <label>Subcategories</label>
                {editCategory.subcategories && editCategory.subcategories.map((subcat, index) => (
                  <div key={index} className={classes.subcategoryItem}>
                    <Chip
                      label={subcat.name}
                      onDelete={() => handleEditRemoveSubcategory(index)}
                      deleteIcon={<CancelIcon />}
                    />
                    <TextField
                      style={{ marginLeft: '16px', flexGrow: 1 }}
                      placeholder="Support link"
                      value={subcat.link}
                      onChange={(e) => handleEditSubcategoryChange(index, 'link', e.target.value)}
                    />
                    <TextField
                      style={{ marginLeft: '16px', flexGrow: 1 }}
                      placeholder="Video URL"
                      value={subcat.videoLink}
                      onChange={(e) => handleEditSubcategoryChange(index, 'videoLink', e.target.value)}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isPaid"
                          checked={subcat.isPaid || false}
                          onChange={(e) => handleEditSubcategoryChange(index, 'isPaid', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="isPaid"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="showExternally"
                          checked={subcat.showExternally || false}
                          onChange={(e) => handleEditSubcategoryChange(index, 'showExternally', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="show Externally"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="needFeedback"
                          checked={subcat.needFeedback || false}
                          onChange={(e) => handleEditSubcategoryChange(index, 'needFeedback', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="needFeedback"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="scheduleMeeting"
                          checked={subcat.scheduleMeeting || false}
                          onChange={(e) => handleEditSubcategoryChange(index, 'scheduleMeeting', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="scheduleMeeting"
                    />

                  </div>
                ))}
                <div className={classes.subcategoryInput}>
                  <TextField
                    fullWidth
                    name="newSubcategoryName"
                    value={editCategory.newSubcategoryName || ""}
                    onChange={handleEditNewSubcategoryChange}
                    placeholder="Subcategory name"
                  />
                  <TextField
                    fullWidth
                    name="newSubcategoryLink"
                    value={editCategory.newSubcategoryLink || ""}
                    onChange={handleEditNewSubcategoryChange}
                    placeholder="Support link"
                  />
                  <FormControlLabel
                      control={
                        <Checkbox
                          name="isPaid"
                          checked={editCategory.isPaid || false}
                          onChange={handleEditNewSubcategoryChange}
                          color="primary"
                        />
                      }
                      label="isPaid"
                    />
                  <IconButton
                    color="primary"
                    onClick={handleEditAddSubcategory}
                    disabled={!(editCategory.newSubcategoryName && editCategory.newSubcategoryName.trim())}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </div>
              </Grid>
              <Grid item xs={12}>
                <label>Visible To Roles</label>
                <div className={classes.roleCheckboxContainer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editCategory.visibleToRoles.owner || false}
                        onChange={handleEditRoleVisibilityChange('owner')}
                        color="primary"
                      />
                    }
                    label="Owner"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editCategory.visibleToRoles.manager || false}
                        onChange={handleEditRoleVisibilityChange('manager')}
                        color="primary"
                      />
                    }
                    label="Manager"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editCategory.visibleToRoles.superAdmin || false}
                        onChange={handleEditRoleVisibilityChange('superAdmin')}
                        color="primary"
                      />
                    }
                    label="Super Admin"
                  />
                </div>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} color="secondary">Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Update</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupportCategory;